"use client";

import { Clock, ExternalLink, Loader2, Phone, RotateCw } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useCallback, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { parseArticleHtml } from "@/components/widget/article-html";
import { buttonKeys } from "@/components/widget/quick-replies/button-keys";
import {
  CHIP_HIT_AREA,
  CHIP_LABEL_CLAMP,
  CHIP_TOUCH_GAP_THREAD,
} from "@/components/widget/quick-replies/chip-classes";
import { useQuickReplyGroupProps } from "@/components/widget/quick-replies/use-quick-reply-group-props";
import { useTracking } from "@/components/widget/tracking/tracking-provider";
import type { MockMessage, MockMessageButton, MockMessageMedia } from "@/dummy";
import { AI_AGENT_ROLE } from "@/lib/ai-assistant";
import { type InternalLink, parseInternalLink } from "@/lib/internal-link";
import { containsHtml, looksLikeMarkdown, normalizeMessageHtml } from "@/lib/message-html";
import { openExternalUrl } from "@/lib/safe-open";
import { cn } from "@/lib/utils";
import { isLinkButton } from "../message-mapping";
import { type Entrance, entranceAttrs } from "../row-entrance";
import { BotAvatar } from "./bot-avatar";
import { fitMedia } from "./media-box";
import type { LightboxItem } from "./media-lightbox";
import { MessageSources } from "./sources/message-sources";
import { VideoTile } from "./video-tile";
import { VoiceNote } from "./voice-note";

export function MessageBubble({
  message,
  organizationLogoUrl,
  organizationName,
  aiAssistantAvatarUrl,
  aiAssistantName,
  buttonsActive,
  onButtonClick,
  onOpenLightbox,
  onInternalLink,
  onRetry,
  enter,
}: {
  message: MockMessage;
  organizationLogoUrl?: string | null;
  organizationName?: string;
  /** Operator-uploaded AI assistant avatar URL — surfaced on AI/bot
   *  bubbles when present. When `null` / `undefined`, AI bubbles
   *  render without an avatar (the column collapses). */
  aiAssistantAvatarUrl?: string | null;
  /** Resolved AI agent name (from `config.messenger.aiAssistant`). Shown on
   *  AI/bot bubbles; `null`/empty when the operator disabled the agent identity
   *  (then only the "AI Agent" role + time show). */
  aiAssistantName?: string | null;
  /** When `true`, the bubble's `buttons` render as clickable chips
   *  (latest outbound run); otherwise they're hidden. */
  buttonsActive?: boolean;
  onButtonClick?: (button: MockMessageButton) => void;
  /** Opens the full-screen gallery overlay starting at the given
   *  index inside the message's visual media batch. */
  onOpenLightbox?: (items: LightboxItem[], index: number) => void;
  /** Called when the visitor taps a widget-internal link inside the
   *  message body (e.g. AI reply pointing at `/help/foo/bar`). The
   *  shell wires it to `setRoute(...)` so the article opens inside
   *  the widget instead of triggering a browser navigation to the
   *  widget's own origin (which would 404). */
  onInternalLink?: (link: InternalLink) => void;
  /** Retry a failed send — shown on the visitor's own bubble when the outbox
   *  couldn't deliver it. Wired up through the tab to the offline queue. */
  onRetry?: () => void;
  /** Entrance of this row (delay in ms; `null`/absent = static). See `row-entrance.ts`. */
  enter?: Entrance;
}) {
  const t = useTranslations("messages");
  const quickReplyGroup = useQuickReplyGroupProps();
  const entrance = entranceAttrs(enter);
  const isOutbound = message.direction === "OUTBOUND";
  // Whether an avatar column is actually drawn next to this outbound bubble.
  // Operators always render one (photo / monogram / org logo); an AI/bot only
  // when the operator uploaded an assistant avatar. When there's none, the
  // avatar collapses and the bubble + meta sit flush-left (no empty gutter).
  const isAiSender = message.sender.kind === "ai" || message.sender.kind === "bot";
  const showBotAvatar = isOutbound && (!isAiSender || !!aiAssistantAvatarUrl);
  // Left indent that clears the avatar gutter — `ml-9` (avatar + gap) when an
  // avatar is drawn, a hair (`ml-1`) when it collapsed.
  const outboundIndent = showBotAvatar ? "ml-9" : "ml-1";
  // Offline-queue states on the visitor's OWN bubble: QUEUED (waiting for a
  // connection), SENDING (an attempt is in flight) and FAILED (send gave up —
  // offer a manual retry). Never shown on received (operator/AI) bubbles.
  const isQueued = !isOutbound && message.deliveryStatus === "QUEUED";
  const isSending = !isOutbound && message.deliveryStatus === "SENDING";
  const isFailed = !isOutbound && message.deliveryStatus === "FAILED";
  // Link buttons (`url` / `phone`) render ALWAYS — they open a link / dial and
  // don't depend on the flow being at this card. Callback (and any other)
  // buttons render only while this message is the active flow step
  // (`buttonsActive`), since tapping a stale callback shouldn't resurrect an
  // old prompt.
  const visibleButtons = (message.buttons ?? []).filter(
    (btn) => isLinkButton(btn) || buttonsActive,
  );
  const visibleKeys = buttonKeys(visibleButtons);

  // Dispatch a "Sources" card click the same way the reply's inline links go:
  // an internal `/help` · `/news` path routes through the in-widget viewer
  // (and gets tracked downstream by the detail view), an absolute URL opens in
  // a new tab and is reported as an EXTERNAL widget-link click.
  const { trackLinkClick } = useTracking();
  const openSourceLink = useCallback(
    (link: string) => {
      const internal = parseInternalLink(link);
      if (internal) {
        onInternalLink?.(internal);
        return;
      }
      trackLinkClick({ url: link, targetType: "EXTERNAL" });
      openExternalUrl(link);
    },
    [onInternalLink, trackLinkClick],
  );

  // Tap the bubble to toggle its sender + time meta line (hidden by default to
  // keep the thread clean — the classic messenger "tap to reveal timestamp").
  const [showMeta, setShowMeta] = useState(false);
  const toggleMeta = useCallback((event: React.MouseEvent) => {
    // Ignore taps that land on an interactive child — links open, media
    // buttons open the lightbox; only a tap on the bubble body itself toggles.
    if ((event.target as HTMLElement).closest("a, button, input, textarea, video")) return;
    setShowMeta((v) => !v);
  }, []);
  // AI / bot ALWAYS reads as AI_ASSISTANT_NAME regardless of whatever label the
  // BE attached ("Bot", "WEB widget", an internal handle, …). The
  // history mapper + SSE consumer already pre-relabel, but bubble-
  // shaped messages from other entry points (preview pane, demo) may
  // not — re-assert here as a safety net so a stray "Bot" can't leak.
  // Operator names flow through; empty operator name falls back to
  // the role label below.
  const displayName = isAiSender
    ? (aiAssistantName ?? "") // configured agent name; "" when the agent is off
    : message.sender.name?.trim() || "";
  // Role suffix. Everyone who is NOT an operator reads as "AI Agent" — shown
  // ALONGSIDE the name ("Gaia • AI Agent • 16:44"), or on its own when the
  // agent is disabled (no name). An operator only gets a role when we have no
  // name to show for them.
  const senderRoleLabel = !isOutbound
    ? null
    : message.sender.kind === "operator"
      ? displayName
        ? null
        : t("operator")
      : AI_AGENT_ROLE;
  return (
    <motion.div
      // No `layout="position"`: it made every bubble a framer projection node that
      // is re-measured (getBoundingClientRect) on every tree update - cost linear
      // in the thread length for no visible effect, since rows are in normal flow
      // and nothing above the newest bubble moves when it arrives.
      // `data-message-id` is the lookup the tab's scroll effect uses to find
      // a newly-arrived bubble (querying by id) so it can scroll the TOP of
      // long operator/AI messages into view, instead of pinning the bottom.
      data-message-id={message.id}
      // Entrance is a CSS animation (`wx-gentle-in`, llm-console's fade + 5px rise), not a JS
      // spring: it costs nothing per row and only runs when the element mounts. Motion keeps
      // the exit (a removed message fades).
      initial={false}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.24, ease: "easeOut" }}
      style={entrance.style}
      className={cn(
        "flex flex-col gap-1.5",
        isOutbound ? "items-start" : "items-end",
        entrance.className,
      )}
    >
      <div
        className={cn(
          "flex max-w-[85%] items-start gap-2",
          isOutbound ? "flex-row" : "flex-row-reverse",
        )}
      >
        {isOutbound && (
          <BotAvatar
            size={28}
            senderKind={message.sender.kind}
            senderName={message.sender.name}
            senderAvatarUrl={message.sender.avatar}
            aiAssistantAvatarUrl={aiAssistantAvatarUrl}
            organizationLogoUrl={organizationLogoUrl}
            organizationName={organizationName}
            className="mt-0.5"
          />
        )}
        {(() => {
          // Split media into VISUAL (image / video) vs NON-VISUAL
          // (audio / document). Visual renders FULL-BLEED at the top
          // of the bubble; caption + non-visual media sit padded
          // below. When neither caption nor non-visual content
          // exists, the bubble background drops away and only the
          // rounded visual remains — matches modern messenger UIs.
          const allMedia = message.media ?? [];
          // PDFs are previewable in the lightbox (iframe-rendered), so they
          // join image/video at the FULL-BLEED top of the bubble instead of
          // sitting inset in the padded section. Audio + other docs still
          // render inside the padded `nonVisual` block below.
          const isVisual = (m: MockMessageMedia) =>
            m.mimetype.startsWith("image/") ||
            m.mimetype.startsWith("video/") ||
            m.mimetype === "application/pdf";
          const visual = allMedia.filter(isVisual);
          const nonVisual = allMedia.filter((m) => !isVisual(m));
          const hasCaption = !!message.text;
          const hasNonVisual = nonVisual.length > 0;
          const showBubbleBg = hasCaption || hasNonVisual;
          return (
            // biome-ignore lint/a11y/noStaticElementInteractions: optional reveal — tap the bubble to show the supplementary sender/time meta; links + media inside keep their own actions (guarded in `toggleMeta`)
            <div
              onClick={toggleMeta}
              className={cn(
                "max-w-full overflow-hidden rounded-wx-lg",
                showBubbleBg &&
                  (isOutbound
                    ? "bg-wx-bg-elevated text-wx-fg"
                    : "bg-wx-primary text-wx-primary-fg"),
              )}
            >
              {visual.length > 0 && (
                <VisualMediaBlock items={visual} onOpenLightbox={onOpenLightbox} />
              )}
              {(hasCaption || hasNonVisual) && (
                <div
                  className={cn(
                    "flex flex-col gap-2 px-4 py-2.5 text-sm",
                    // Tighter top padding when visual media sits
                    // above — keeps caption read as a label on the
                    // image, not a free-floating bubble.
                    visual.length > 0 && "pt-2",
                  )}
                >
                  {hasCaption && (
                    <MessageBody
                      text={message.text}
                      isOutbound={isOutbound}
                      onInternalLink={onInternalLink}
                    />
                  )}
                  {nonVisual.map((m, idx) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: per-message media is fixed-order and never reordered
                    <Media key={idx} media={m} onDark={!isOutbound} />
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </div>
      {/* Sender + time meta line — hidden by default, revealed by tapping the
          bubble (`toggleMeta`). Indents to match the bubble's left edge (after
          the avatar) on outbound; right-aligns flush with the visitor bubble
          on inbound. */}
      <AnimatePresence initial={false}>
        {showMeta && (displayName || senderRoleLabel || message.ageLabel) && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className={cn(
              "flex items-center gap-1.5 overflow-hidden text-[11px] text-wx-fg-subtle",
              isOutbound ? outboundIndent : "mr-1",
            )}
          >
            {isOutbound && displayName && (
              <span className="font-medium text-wx-fg-muted">{displayName}</span>
            )}
            {isOutbound && senderRoleLabel && (
              <>
                {displayName && <span aria-hidden="true">·</span>}
                <span>{senderRoleLabel}</span>
              </>
            )}
            {message.ageLabel && (
              <>
                {(isOutbound && displayName) || (isOutbound && senderRoleLabel) ? (
                  <span aria-hidden="true">·</span>
                ) : null}
                <span>{message.ageLabel}</span>
              </>
            )}
          </motion.p>
        )}
      </AnimatePresence>
      {/* Offline-queue status on the visitor's own bubble — right-aligned to sit
          flush under it. QUEUED shows a quiet "waiting for connection"; FAILED
          shows a warning + a Retry action wired to the outbox. */}
      {(isQueued || isSending || isFailed) && (
        // Fixed-height slot so the row keeps its height across queued → sending
        // → failed — otherwise the footer collapsed mid-attempt and jumped the
        // scroll when the visitor hit Retry. Compact: a single line, the retry
        // is an icon affordance (no second "retry" word) rather than a full row.
        <div className="mr-1 flex h-4 items-center justify-end gap-1 text-[11px] leading-none">
          {isFailed ? (
            <span className="flex items-center gap-1 text-wx-danger">
              <span>{t("notDelivered")}</span>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  aria-label={t("retry")}
                  title={t("retry")}
                  className="-m-0.5 flex items-center rounded-full p-0.5 transition-opacity hover:opacity-70"
                >
                  <RotateCw aria-hidden="true" className="size-3" />
                </button>
              )}
            </span>
          ) : isSending ? (
            <Loader2 aria-hidden="true" className="size-3 animate-spin text-wx-fg-subtle" />
          ) : (
            <span className="flex items-center gap-1 text-wx-fg-subtle">
              <Clock aria-hidden="true" className="size-3" />
              <span>{t("queued")}</span>
            </span>
          )}
        </div>
      )}
      {message.sources && message.sources.length > 0 && (
        // AI-reply citations sit under the bubble, above any quick-reply
        // buttons. `ml-9` aligns them under the bubble past the avatar gutter
        // (sources are AI-only, so always the outbound/left side). The
        // max-width caps the block to the bubble's own right edge — `85%` (the
        // bubble's max-width, a % of the message column) minus the `2.25rem`
        // avatar+gap the `ml-9` already consumed — so long source URLs truncate
        // instead of running to the widget's edge.
        <div
          className={cn(showBotAvatar ? "ml-9 max-w-[calc(85%_-_2.25rem)]" : "ml-1 max-w-[85%]")}
        >
          <MessageSources sources={message.sources} onOpen={openSourceLink} />
        </div>
      )}
      {visibleButtons.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          {...quickReplyGroup}
          className={cn(
            "flex flex-wrap gap-1",
            CHIP_TOUCH_GAP_THREAD,
            isOutbound ? cn(outboundIndent, "max-w-[85%]") : "mr-1 justify-end",
          )}
        >
          {visibleButtons.map((btn, i) => (
            <motion.button
              // Slot quick replies carry no payload (the label is sent), so the payload alone collides.
              key={visibleKeys[i]}
              type="button"
              onClick={() => onButtonClick?.(btn)}
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.96 }}
              transition={{ type: "spring", stiffness: 480, damping: 26 }}
              className={cn(
                // Inside the conversation the chips read as
                // satellites of the operator bubble — so they take
                // the SAME `bg-wx-bg-elevated` surface as the
                // bubble itself (the launcher peek uses bare
                // `bg-wx-bg` because it sits on the host page, not
                // inside a panel). Hover fills with full brand.
                "inline-flex max-w-full items-center gap-1",
                "rounded-full bg-wx-bg-elevated px-2.5 py-1 text-xs font-medium text-wx-primary",
                CHIP_HIT_AREA,
                "transition-colors hover:bg-wx-primary hover:text-wx-primary-fg",
              )}
            >
              {/* Link / phone chips carry a glyph so a visitor can tell they
                  leave the chat (open a page / dial) rather than answer the
                  flow — callback chips stay bare. */}
              {btn.type === "url" && (
                <ExternalLink size={12} className="shrink-0" aria-hidden="true" />
              )}
              {btn.type === "phone" && <Phone size={12} className="shrink-0" aria-hidden="true" />}
              <span dir="auto" className={CHIP_LABEL_CLAMP}>
                {btn.text}
              </span>
            </motion.button>
          ))}
        </motion.div>
      )}
    </motion.div>
  );
}

/**
 * Renders message body text. Detects HTML ANYWHERE in the text (not only a
 * leading tag): operator/bot messages from the dashboard TipTap editor arrive
 * as full HTML (`<p class="tiptap-paragraph">…`), while AI replies are plain
 * text with `<a>` / `<ul>` / `<pre>` fragments embedded mid-text — both need
 * parsing, not literal display. Plain-text replies (visitor input, short AI
 * answers) keep the pre-wrap paragraph styling.
 *
 * The HTML branch runs the body through `parseArticleHtml` — the same
 * `html-react-parser` pipeline the article / operator HTML surfaces use, so
 * markup becomes a React tree (`<script>` is inert, links open safely) rather
 * than `dangerouslySetInnerHTML`. Content is sanitised on the BE before
 * persistence. The widget's `prose`-style ruleset clamps heading / list
 * spacing to the bubble geometry so a paragraph doesn't blow out the chat
 * width.
 */
function MessageBody({
  text,
  isOutbound,
  onInternalLink,
}: {
  text: string;
  isOutbound: boolean;
  onInternalLink?: (link: InternalLink) => void;
}) {
  // Delegate click handling on the bubble body so any `<a>` embedded
  // by the operator / AI reply gets a chance to route through the
  // widget instead of letting the browser navigate. Modifier-clicks
  // (cmd / ctrl / shift / middle button) fall through to default
  // behaviour — those are explicit "open elsewhere" gestures and the
  // visitor's intent already overrides ours.
  const { trackLinkClick } = useTracking();
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (event.button !== 0) return;
      const anchor = (event.target as HTMLElement | null)?.closest?.("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      const internal = parseInternalLink(href);
      if (internal) {
        // Widget-internal link (e.g. a `/help/.../slug` article) → route
        // in-widget instead of navigating. Without a handler, fall through
        // to the browser. Not tracked here: internal links land on a detail
        // view (help/news), whose own open-tracking effect fires HELP_ARTICLE
        // / NEWS_POST keyed on the resolved id.
        if (!onInternalLink) return;
        event.preventDefault();
        onInternalLink(internal);
        return;
      }
      // External link (operator- or AI/copilot-authored `<a>` in the reply)
      // → open in a new tab so the widget iframe doesn't navigate away from
      // the conversation, and report it as an EXTERNAL widget-link click
      // (no-op unless the operator's `trackWidgetLinks` flag is on).
      event.preventDefault();
      trackLinkClick({ url: href, targetType: "EXTERNAL" });
      openExternalUrl(href);
    },
    [onInternalLink, trackLinkClick],
  );

  const html = containsHtml(text);
  // The AI usually sends HTML but sometimes emits Markdown; render that (for
  // outbound AI/operator messages only, so a visitor's literal `**` stays
  // plain) into the SAME styled container below, so Markdown and HTML look
  // identical. A visitor's own plain text never markdown-parses.
  const markdown = !html && isOutbound && looksLikeMarkdown(text);

  // Neither HTML nor Markdown → plain text. Keep the pre-wrap paragraph so
  // `\n` line breaks survive; React escapes it, so nothing renders as markup.
  if (!html && !markdown) {
    // `overflow-wrap:anywhere` so a long unbroken string / URL wraps inside the bubble
    // instead of running past it (the bubble clips overflow).
    return <p className="whitespace-pre-wrap leading-relaxed [overflow-wrap:anywhere]">{text}</p>;
  }
  // Rich body — one styled + click-delegating container shared by both:
  //   - HTML: operator TipTap content OR an AI reply with embedded `<a>` /
  //     `<ul>` / `<pre>` fragments, parsed by `parseArticleHtml` (safe
  //     html-react-parser pipeline — links open safely, `<script>` is inert).
  //   - Markdown: rendered by `react-markdown` into the SAME element set, so
  //     the `[&_…]` styles + link delegation apply identically.
  // HTML stays `pre-wrap` (bare AI text mixes `\n` with tags); Markdown is
  // fully structured, so `normal` whitespace — pre-wrap would re-introduce
  // gaps between react-markdown's block siblings.
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: click is a delegating dispatcher — the underlying `<a>` is keyboard-activatable natively, the wrapper just intercepts mouse-driven default navigation
    <div
      onClick={handleClick}
      className={cn(
        // `anywhere` wraps long links / unbroken words; tables and code blocks scroll
        // sideways INSIDE the bubble instead of widening it.
        "leading-relaxed [overflow-wrap:anywhere]",
        "[&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto",
        // The bubble's `anywhere` wrapping is inherited into the cells, which makes their
        // min-content one character: the table squeezed into the bubble and broke words mid-word.
        // Wrap cells at spaces only, so a table wider than the bubble scrolls inside it.
        "[&_td]:[overflow-wrap:normal] [&_th]:[overflow-wrap:normal]",
        html ? "whitespace-pre-wrap" : "whitespace-normal",
        // Tighten editor-spawned block elements to match bubble cadence.
        "[&>p]:my-0 [&>p+p]:mt-2",
        "[&_a]:underline [&_a]:underline-offset-2 [&_a]:opacity-90 [&_a:hover]:opacity-100",
        "[&_strong]:font-semibold [&_em]:italic [&_u]:underline [&_s]:line-through",
        // Monospace chip — matches the composer's `<code>` styling.
        "[&_code]:rounded-wx-sm [&_code]:bg-wx-bg-elevated-2 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em]",
        // Lists render in NORMAL whitespace mode (the container is
        // `pre-wrap` for the AI's bare-text `\n` runs). Without this, the
        // indentation whitespace the AI leaves between `<li>` tags
        // (`</li>  <li>`) is preserved by `pre-wrap` as empty line boxes →
        // huge gaps between bullets. `normal` collapses that inter-element
        // whitespace the way standard HTML does, leaving just `[&_li]:my-0.5`.
        "[&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:whitespace-normal [&_ul]:pl-5",
        "[&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:whitespace-normal [&_ol]:pl-5",
        "[&_li]:my-0.5",
        // Some AI "loose" lists instead wrap each item in `<li><p>…</p></li>`;
        // the direct-child reset above doesn't reach those nested paragraphs,
        // so flatten them too (tiny gap if an item genuinely has two).
        "[&_li>p]:my-0 [&_li>p+p]:mt-1",
        "[&_code]:rounded-sm [&_code]:bg-black/10 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.85em]",
        "[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:[overflow-wrap:normal] [&_pre]:rounded [&_pre]:bg-black/10 [&_pre]:p-2 [&_pre]:text-[0.8em]",
        // The visitor's own (right) bubble is `bg-wx-primary` — the parsed
        // link's own `text-wx-primary` would be dark-on-dark (invisible), so
        // force links to the bubble's foreground colour and keep code contrast.
        !isOutbound && "[&_code]:bg-white/15 [&_pre]:bg-white/15 [&_a]:text-wx-primary-fg",
      )}
    >
      {html ? (
        parseArticleHtml(normalizeMessageHtml(text))
      ) : (
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
      )}
    </div>
  );
}

/**
 * Renders the visual (image / video) media stack at the top of a
 * message bubble. Single item → full-width tile that triggers the
 * lightbox on tap; multiple items → 2-column grid where each cell
 * crops to a sensible thumbnail and opens the lightbox at the
 * right index. Videos get the inline `<video controls>` for quick
 * preview AND the lightbox click affordance.
 */
function VisualMediaBlock({
  items,
  onOpenLightbox,
}: {
  items: { url: string; mimetype: string; alt: string }[];
  onOpenLightbox?: (items: LightboxItem[], index: number) => void;
}) {
  const t = useTranslations("messages");
  if (items.length === 0) return null;
  const multi = items.length > 1;
  return (
    <div
      className={cn(
        "grid w-full",
        multi ? "max-h-[20rem] grid-cols-2 gap-0.5 auto-rows-fr" : "grid-cols-1",
      )}
    >
      {items.map((item, idx) => {
        const handleOpen = () => onOpenLightbox?.(items, idx);
        // PDF — full-bleed clickable card; opens the lightbox (iframe). No
        // inline preview thumbnail (we'd need PDF.js to rasterise the first
        // page) so we show a "PDF" badge + filename + mimetype.
        if (item.mimetype === "application/pdf") {
          return (
            <button
              type="button"
              // biome-ignore lint/suspicious/noArrayIndexKey: per-message media is fixed-order and never reordered
              key={idx}
              onClick={handleOpen}
              aria-label={item.alt || t("openPreview")}
              className={cn(
                "group flex w-full cursor-zoom-in items-center gap-3 px-4 py-3 text-left",
                "bg-wx-bg-elevated text-wx-fg transition-colors hover:bg-wx-bg-elevated-2",
              )}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-wx-sm bg-wx-bg text-[10px] font-bold tracking-wider text-wx-fg-muted shadow-sm">
                PDF
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium">{item.alt}</span>
                <span className="truncate text-xs text-wx-fg-muted">{item.mimetype}</span>
              </span>
            </button>
          );
        }
        if (item.mimetype.startsWith("video/")) {
          return (
            <VideoTile
              // biome-ignore lint/suspicious/noArrayIndexKey: per-message media is fixed-order and never reordered
              key={idx}
              item={item}
              multi={multi}
              onOpen={handleOpen}
              label={item.alt || t("openPreview")}
            />
          );
        }
        return (
          <ImageTile
            // biome-ignore lint/suspicious/noArrayIndexKey: per-message media is fixed-order and never reordered
            key={idx}
            item={item}
            multi={multi}
            onOpen={handleOpen}
            label={item.alt || t("openImage")}
          />
        );
      })}
    </div>
  );
}

/** Default width for a single image whose intrinsic size is unknown (an SSE
 *  row before the BE serves dims). Gives the shrink-to-fit bubble a basis so
 *  the tile reserves a box instead of collapsing to 0px. */
const FALLBACK_IMAGE_WIDTH = 220;

/**
 * Single/grid image tile.
 *
 * SINGLE + known dims: rendered at its NATURAL aspect (never cropped). The
 * intrinsic `width`/`height` (BE Media doc / Giphy preview) are set as the img's
 * HTML attributes so the browser reserves the aspect-ratio box BEFORE the pixels
 * load — no jump, no shove. The img keeps its NATURAL width (`w-auto`, not
 * `w-full`): a `width:100%` img contributes no intrinsic width to the
 * shrink-to-fit bubble, which silently defeats the reservation.
 *
 * SINGLE + unknown dims: reserve a fixed-width box at a default ratio (never
 * 0px) and settle it to the natural ratio once the image reports its size — so
 * a dim-less row no longer flashes from 0 to full height.
 *
 * GRID tiles keep their fixed-cell, cropped-thumbnail layout.
 */
function ImageTile({
  item,
  multi,
  onOpen,
  label,
}: {
  item: MockMessageMedia;
  multi: boolean;
  onOpen: () => void;
  label: string;
}) {
  const hasDims = !!(item.width && item.height);
  // Measured aspect-ratio for the dim-less case, filled in on load.
  const [measured, setMeasured] = useState<string | null>(null);

  if (multi) {
    return (
      <button
        type="button"
        onClick={onOpen}
        aria-label={label}
        className="group relative block w-full cursor-zoom-in overflow-hidden"
      >
        {/* biome-ignore lint/performance/noImgElement: operator/visitor message media of arbitrary host/dimensions; next/image needs known sizes + an allowlist */}
        <img
          src={item.url}
          alt={item.alt}
          loading="lazy"
          className="block h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
        />
      </button>
    );
  }

  if (hasDims && item.width && item.height) {
    // Compute the EXACT display size from the intrinsic dims, capped to
    // MAX_MEDIA_PX on both sides while preserving aspect. The container gets
    // that size as explicit `width`/`height` px — a DEFINITE box, so it holds
    // its space before the image loads (Tailwind `w-auto`/`h-auto` otherwise
    // override the width/height attributes → an unloaded img measures 0×0 and
    // the bubble collapses, then jumps on load). The image fills the box
    // exactly (box already matches its aspect, so `object-cover` never crops).
    const { width: dw, height: dh } = fitMedia(item.width, item.height);
    return (
      <button
        type="button"
        onClick={onOpen}
        aria-label={label}
        // Explicit width (definite → reserves before load) + aspect-ratio so a
        // narrow viewport scales BOTH sides down together (`maxWidth:100%`),
        // never cropping.
        style={{ width: `${dw}px`, aspectRatio: `${dw} / ${dh}`, maxWidth: "100%" }}
        className="group relative block cursor-zoom-in overflow-hidden"
      >
        {/* biome-ignore lint/performance/noImgElement: operator/visitor message media of arbitrary host/dimensions; next/image needs known sizes + an allowlist */}
        <img
          src={item.url}
          alt={item.alt}
          loading="lazy"
          className="absolute inset-0 block h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
        />
      </button>
    );
  }

  // Unknown dims — reserve a fixed-width box (default square until the image
  // loads) so it never collapses to 0px; settle to the natural ratio on load.
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label}
      style={{
        width: `${FALLBACK_IMAGE_WIDTH}px`,
        maxWidth: "100%",
        aspectRatio: measured ?? "1 / 1",
        maxHeight: "20rem",
      }}
      className="group relative block cursor-zoom-in overflow-hidden"
    >
      {/* biome-ignore lint/performance/noImgElement: operator/visitor message media of arbitrary host/dimensions; next/image needs known sizes + an allowlist */}
      <img
        src={item.url}
        alt={item.alt}
        loading="lazy"
        onLoad={(e) => {
          const el = e.currentTarget;
          if (el.naturalWidth && el.naturalHeight) {
            setMeasured(`${el.naturalWidth} / ${el.naturalHeight}`);
          }
        }}
        className="absolute inset-0 block h-full w-full object-contain transition-transform duration-200 group-hover:scale-[1.02]"
      />
    </button>
  );
}

/**
 * Renderer for NON-VISUAL message media (audio / documents). Visual
 * media (image / video) is handled upstream by `VisualMediaBlock`
 * so it can sit full-bleed at the top of the bubble; this helper
 * stays focused on the in-bubble item types where the bubble
 * background needs to stick.
 */
function Media({
  media,
  onDark,
}: {
  media: { url: string; mimetype: string; alt: string };
  /** True when the surrounding bubble uses `bg-wx-primary` (visitor's
   *  own message). Forwarded to the audio player so it inverts its
   *  palette. */
  onDark?: boolean;
}) {
  if (media.mimetype.startsWith("audio/")) {
    // `VoiceNote` stays a light static row until the visitor interacts with it;
    // only then does it fetch `wavesurfer.js` and decode the file.
    return <VoiceNote src={media.url} onDark={onDark} />;
  }
  return <FileCard media={media} />;
}

/**
 * Generic file card. Used for documents (PDF, docx, xlsx, txt, etc.)
 * — anything the visitor can't preview inline. The icon hints at
 * mimetype family; tap → open in a new tab.
 */
export function FileCard({ media }: { media: { url: string; mimetype: string; alt: string } }) {
  const t = useTranslations("messages");
  const label = media.alt || media.url.split("/").pop() || media.mimetype || t("fileFallback");
  const typeChip = mimetypeChip(media.mimetype);
  return (
    <a
      href={media.url}
      target="_blank"
      rel="noopener"
      className="flex max-w-xs items-center gap-3 rounded-wx-sm bg-wx-bg-elevated/60 px-3 py-2.5 text-xs text-wx-fg transition-colors hover:bg-wx-bg-elevated"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-wx-sm bg-wx-bg-elevated-2 text-[10px] font-bold tracking-wider text-wx-fg-muted uppercase">
        {typeChip}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-wx-fg">{label}</span>
        <span className="block truncate text-wx-fg-subtle">
          {media.mimetype || t("fileFallback")}
        </span>
      </span>
    </a>
  );
}

function mimetypeChip(mimetype: string): string {
  if (!mimetype) return "FILE";
  if (mimetype.includes("pdf")) return "PDF";
  if (mimetype.includes("zip") || mimetype.includes("compressed")) return "ZIP";
  if (mimetype.includes("spreadsheet") || mimetype.includes("excel")) return "XLS";
  if (mimetype.includes("word") || mimetype.includes("document")) return "DOC";
  if (mimetype.startsWith("text/")) return "TXT";
  const sub = mimetype.split("/")[1] ?? "";
  return sub.slice(0, 4).toUpperCase() || "FILE";
}
